import React from 'react';
import { Info } from 'lucide-react';

/**
 * AquaManagerFishComparison — small validation tile that surfaces the
 * divergence between the operator's vendor-reported fish count (AquaManager
 * or Fishtalk) and PFD's best-fit (analytical) fish count.
 *
 * Read-only — neither number is overwritten.
 * Renders nothing if either input is missing.
 *
 * Supports both AquaManager and Fishtalk sources. Pass either
 * `aquamanagerFishNumber` OR `fishtalkFishNumber` (or both — the heading
 * adapts to whichever vendor is set).
 */
export default function AquaManagerFishComparison({
  aquamanagerFishNumber,
  aquamanagerFishNumberDate,
  fishtalkFishNumber,
  fishtalkFishNumberDate,
  pfdBestFitFishNumber,
}) {
  // Pick whichever vendor source is populated
  let operatorFishNumber = null;
  let operatorFishNumberDate = null;
  let vendorLabel = 'Operator';
  if (aquamanagerFishNumber !== null && aquamanagerFishNumber !== undefined) {
    operatorFishNumber = aquamanagerFishNumber;
    operatorFishNumberDate = aquamanagerFishNumberDate;
    vendorLabel = 'AquaManager';
  } else if (fishtalkFishNumber !== null && fishtalkFishNumber !== undefined) {
    operatorFishNumber = fishtalkFishNumber;
    operatorFishNumberDate = fishtalkFishNumberDate;
    vendorLabel = 'Fishtalk';
  }

  if (
    operatorFishNumber === null || operatorFishNumber === undefined ||
    pfdBestFitFishNumber === null || pfdBestFitFishNumber === undefined
  ) {
    return null;
  }

  const diff = pfdBestFitFishNumber - operatorFishNumber;
  const diffPct = operatorFishNumber > 0
    ? (diff / operatorFishNumber) * 100
    : 0;
  const absDiffPct = Math.abs(diffPct);
  let tone;
  if (absDiffPct < 1) tone = 'good';
  else if (absDiffPct < 5) tone = 'warn';
  else tone = 'bad';

  const toneClasses = {
    good: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    warn: 'border-amber-300 bg-amber-50 text-amber-900',
    bad: 'border-red-300 bg-red-50 text-red-900',
  }[tone];

  return (
    <div
      className={`rounded-lg border p-4 ${toneClasses}`}
      data-testid="aquamanager-fish-comparison-tile"
    >
      <div className="flex items-start gap-2 mb-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="text-sm font-semibold">
            {vendorLabel} vs PFD Best-Fit Fish Number
          </p>
          <p className="text-[11px] opacity-80">
            Validation signal only — neither number is overwritten by the other.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wide opacity-70">{vendorLabel} (operator)</p>
          <p className="text-lg font-bold font-mono" data-testid="aqm-fish-no-value">
            {operatorFishNumber.toLocaleString()}
          </p>
          {operatorFishNumberDate && (
            <p className="text-[10px] opacity-70 font-mono">{operatorFishNumberDate}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide opacity-70">PFD best-fit (analytical)</p>
          <p className="text-lg font-bold font-mono" data-testid="pfd-fish-no-value">
            {pfdBestFitFishNumber.toLocaleString()}
          </p>
          <p className="text-[10px] opacity-70">Missing Fish Intelligence output</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-current/20 flex justify-between text-sm">
        <span>
          <span className="opacity-70">Difference:</span>{' '}
          <strong className="font-mono" data-testid="aqm-vs-pfd-diff">{diff >= 0 ? '+' : ''}{diff.toLocaleString()}</strong>{' '}
          fish
        </span>
        <span>
          <span className="opacity-70">Divergence:</span>{' '}
          <strong className="font-mono" data-testid="aqm-vs-pfd-diff-pct">
            {diff >= 0 ? '+' : ''}{diffPct.toFixed(2)}%
          </strong>
        </span>
      </div>
    </div>
  );
}
